import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from "react";
import styles from './datePickerStyle.module.scss';
import { DateTime } from "luxon";
import Tooltip from "../tooltips/Tooltip";
import { useTranslation } from "react-i18next";
import BaseIconButton from "../buttons/iconButtons/BaseIconButton";
import { MultiplyIcon } from "../../libs/corporate/icons/outlined/suggested/SymbolCollection";

interface IDatePickerProps {
    date?: DateTime | null
    onChange?: (value: DateTime | null) => void
    onBlur?: (value: DateTime | null) => void
    onFocus?: () => void
    incorrectPeriod?: boolean
    disabled?: boolean
    isModal?: boolean
    maxDate?: string | number | null
    minDate?: string | number | null
    inputStyle?: CSSProperties
    clearButtonStyle?: CSSProperties
    preventFutureDates ?: boolean
    maxAllowedDate?: DateTime | null
    withTime?: boolean
    isStartDate?: boolean
}

const TIME_SHOW_ERROR = 1000

const formatDateForInput = (date: DateTime | null, withTime: boolean): string => {
    if (!date?.isValid) return '';
    
    return withTime 
        ? date.toFormat("yyyy-MM-dd'T'HH:mm")
        : date.toFormat("yyyy-MM-dd");
};

const parseDateFromInput = (value: string, withTime: boolean): DateTime | null => {
    if (!value) return null;
    
    try {
        let date: DateTime;
        if (withTime) {
            date = DateTime.fromFormat(value, "yyyy-MM-dd'T'HH:mm");
        } else {
            date = DateTime.fromJSDate(new Date(value));
        }
        
        if (!date.isValid) return null;
        
        return date;
    } catch {
        return null;
    }
};

export const DatePicker: FC<IDatePickerProps> = (props) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<string | undefined>('');
    const [sizeVariant, setSizeVariant] = useState<SizeVariant>('mini');
    const ref = useRef<HTMLInputElement>(null);
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const { 
        preventFutureDates, 
        onChange, 
        maxAllowedDate: propMaxAllowedDate,
        date,
        withTime = false        
    } = props;

    useEffect(() => {
        try {
            if (date?.isValid) {
                setValue(formatDateForInput(date, withTime));
            } else {
                setValue('');
            }
        } catch(ex) {
            console.warn("ex", ex);
        }
    }, [date, withTime]);

    
    const [showFutureDateError, setShowFutureDateError] = useState<boolean>(false);
    const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);

    const getMaxAllowedDate = useCallback((): DateTime => {
        return propMaxAllowedDate || DateTime.now();
    }, [propMaxAllowedDate]);

    const validate = (date: DateTime): DateTime => {
        let minDate = DateTime.fromJSDate(new Date(props.minDate as string));
        let maxDate = DateTime.fromJSDate(new Date(props.maxDate as string));
        const maxAllowedDate = getMaxAllowedDate();

        if (preventFutureDates && date > maxAllowedDate) {
            setShowFutureDateError(true);
            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }
            const timeout = setTimeout(() => setShowFutureDateError(false), TIME_SHOW_ERROR);
            setErrorTimeout(timeout);
            return maxAllowedDate;
        }

        if (props.minDate && date <= minDate) {
            return minDate
        } else if ( props.maxDate && date >= maxDate) {
            return maxDate
        } else {
            return date
        }
    }

    useEffect(() => {
        if (value && preventFutureDates) {
            const selectedDate = parseDateFromInput(value, withTime);
            const maxAllowedDate = getMaxAllowedDate();

            if (selectedDate?.isValid && selectedDate > maxAllowedDate) {
                setShowFutureDateError(true);
                if (errorTimeout) {
                    clearTimeout(errorTimeout);
                }
                const timeout = setTimeout(() => setShowFutureDateError(false), TIME_SHOW_ERROR);
                setErrorTimeout(timeout);

                const correctedDate = formatDateForInput(maxAllowedDate, withTime);
                setValue(correctedDate);
                onChange?.(maxAllowedDate);
            }
        }
    }, [
        value, 
        preventFutureDates, 
        getMaxAllowedDate, 
        errorTimeout, 
        onChange,
        withTime
    ]);

    useEffect(() => {
        return () => {
            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }
        };
    }, [errorTimeout]);

    return (
        <>
            <div className={styles.datePicker}>
                <Tooltip tooltipStyle={withTime ? {marginLeft: -7} : {}} title={withTime ? t("general.dateAndTimeSelection") : t("general.dateSelection")}>
                    <input
                        className={showFutureDateError ? styles.error : ''}
                        ref={ref}
                        type={withTime ? "datetime-local" : "date"}
                        style={props.inputStyle}
                        onChange={(date) => {
                            const dateString = date.currentTarget.value;

                            if (isFirefox) {
                                if (dateString !== '') {
                                    setValue(dateString);
                                    const newDate = parseDateFromInput(dateString, withTime);
                                    if (newDate?.isValid) {
                                        const parsedDate = parseDateFromInput(value || '', withTime);
                                        onChange?.(parsedDate);
                                    }
                                } else {
                                    setValue(value);
                                    onChange?.(DateTime.fromJSDate(new Date(value as string || '')));
                                }
                            } else {
                                setValue(dateString);

                                if (dateString) {
                                    const newDate = parseDateFromInput(dateString, withTime);
                                    if (newDate?.isValid) {
                                        const validatedDate = validate(newDate);
                                        onChange?.(validatedDate);
                                    }
                                } else {
                                    onChange?.(null);
                                }
                            }
                        }}
                        onBlur={(date) => {
                            if (typeof date === "string") return;

                            let _date = date.currentTarget.value

                            if (withTime) {
                                const getDefaultDate = (): DateTime => {
                                    if (props.isStartDate) {
                                        return DateTime.fromFormat('1900-01-01 00:00', 'yyyy-MM-dd HH:mm');
                                    } else {
                                        return DateTime.fromFormat('2999-12-31 23:59', 'yyyy-MM-dd HH:mm');
                                    }
                                };

                                if (_date) {
                                    const newDate = parseDateFromInput(_date, withTime);
                                    if (newDate?.isValid) {
                                        const isDefaultDate = newDate.hasSame(DateTime.fromFormat('1900-01-01 00:00', 'yyyy-MM-dd HH:mm'), 'day');

                                        let validatedDate = validate(newDate);

                                        if (isDefaultDate && props.isStartDate) {
                                            validatedDate = validatedDate.set({ hour: 0, minute: 0, second: 0 });
                                        }else if (isDefaultDate && !props.isStartDate) {
                                            validatedDate = validatedDate.set({ hour: 23, minute: 59, second: 59 });
                                        }

                                        const formattedDate = formatDateForInput(validatedDate, withTime);
                                        
                                        if (formattedDate !== _date) {
                                            setValue(formattedDate);
                                            props.onBlur?.(validatedDate);
                                        } else {
                                            props.onBlur?.(validatedDate);
                                        }
                                    } else {
                                        if (props.date) {
                                            setValue(formatDateForInput(props.date, withTime));
                                            props.onBlur?.(props.date);
                                        } else {
                                            const defaultDate = getDefaultDate();
                                            setValue(formatDateForInput(defaultDate, withTime));
                                            props.onBlur?.(defaultDate);
                                        }
                                    }
                                } else {
                                    const defaultDate = getDefaultDate();
                                    setValue(formatDateForInput(defaultDate, withTime));
                                    props.onBlur?.(defaultDate);
                                }
                                return;
                            }
                            let result = date ? validate(DateTime.fromJSDate(new Date(_date))).toFormat("yyyy-MM-dd") : _date
                            if (date.currentTarget.value.length > 10) {
                                setValue(props.maxDate as string);
                                props.onBlur?.(DateTime.fromJSDate(new Date(props.maxDate as string)));
                            } else {
                                if (props.date?.toFormat("yyyy-MM-dd") === _date) {
                                    date.preventDefault();
                                } else {
                                    if (_date) {
                                        const newDate = DateTime.fromJSDate(new Date(_date));
                                        if (newDate.isValid) {
                                            const validatedDate = validate(newDate);
                                            const formattedDate = validatedDate.toFormat("yyyy-MM-dd");

                                            if (formattedDate !== _date) {
                                                setValue(formattedDate);
                                                props.onBlur?.(validatedDate);
                                            } else {
                                                props.incorrectPeriod ? setValue(props.date?.toFormat("yyyy-MM-dd")) : setValue(result);
                                                props.onBlur?.(DateTime.fromJSDate(new Date(result === undefined ? "" : result)));
                                            }
                                        }
                                    } else {
                                        props.incorrectPeriod ? setValue(props.date?.toFormat("yyyy-MM-dd")) : setValue(result);
                                        props.onBlur?.(DateTime.fromJSDate(new Date(result === undefined ? "" : result)));
                                    }
                                }
                            }
                        }}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && ref.current?.blur()}
                        value={value}
                        disabled={props.disabled}
                        max={props.maxDate ? props.maxDate : undefined}
                        min={props.minDate ? props.minDate : undefined}
                        onFocus={props.onFocus}
                        step={withTime ? "60" : undefined}
                    />
                </Tooltip>
                <div
                    style={props.clearButtonStyle}
                    className={styles.dateClearButton}
                >
                    <Tooltip title={t("general.clear")}>
                        <BaseIconButton
                            tabIndex={-1}
                            sizeVariant={props?.clearButtonStyle?.height === 30 ? 'micro' : 'mini'}
                            onClick={() => {
                                setValue('');
                                props.onBlur?.(DateTime.fromJSDate(new Date('')));
                            }}
                            {...props}
                        >
                            <MultiplyIcon />
                        </BaseIconButton>
                    </Tooltip>
                </div>
            </div>
            {showFutureDateError && (
                <div className={styles.datePickerError}>
                    {t("general.futureDateNotAllowed")}
                </div>
            )}
        </>        
    )
}

